/**
 * The Assistant tab's own states around the shared panel (outlook-orachat-uplift
 * s5, proof S9a / S9b, design C15). Light on purpose: App imports this module
 * statically (it is the fallback when the chat chunk itself cannot load), so it
 * must never pull in the panel, MSAL or anything heavy.
 */

import {
  TURN_ERROR_BOX_STYLE,
  TURN_ERROR_FOOTNOTE_STYLE,
} from "@product-app/features/oraChatV2/components/turnErrorStyle";

/** Under the chat's error box, and under the chat-down state: what still works. */
export const NEEDS_YOU_STILL_WORKS = "Needs you still works — your workflows aren't affected.";

/** The chat-down sentence, as the panel's own error box starts. */
export const CHAT_DOWN = "oraChat couldn't complete that — the chat service isn't responding right now.";

/** Every other failed turn (G1-3): no status, no code, no body. */
export const TURN_FAILED = "oraChat couldn't complete that. Please try again in a moment.";

/**
 * The panel's own error box — its one style definition (`turnErrorStyle`), so
 * the two never drift — with "Needs you still works" under it, carrying the
 * pane's sentence.
 */
export function AssistantErrorNotice({
  text,
  className,
}: {
  text: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={className} data-testid="assistant-error-notice">
      <div role="alert" style={TURN_ERROR_BOX_STYLE}>
        {text}
      </div>
      <p data-testid="assistant-still-works" style={TURN_ERROR_FOOTNOTE_STYLE}>
        {NEEDS_YOU_STILL_WORKS}
      </p>
    </div>
  );
}

/** While the chat chunk or the signed-in user loads: one state for the tab and the pane. */
export function AssistantLoading(): JSX.Element {
  return (
    <div className="p-4 text-xs text-primary-gray-light-500" data-testid="assistant-loading">
      Starting oraChat…
    </div>
  );
}

/**
 * S9b: the chat could not start (its chunk failed to load, it threw, or the
 * signed-in user could not be read). The workflow tabs are untouched.
 */
export function AssistantChatDown(): JSX.Element {
  return (
    <div className="p-3" data-testid="assistant-chat-down">
      <AssistantErrorNotice text={CHAT_DOWN} />
    </div>
  );
}

/**
 * S9a: the chat's silent sign-in ran out. "Sign in" runs the add-in's existing
 * interactive sign-in (`onSignIn`). Once that succeeds the card says so, and the
 * next question goes through.
 */
export function AssistantSignInCard({
  signedIn = false,
  onSignIn,
}: {
  signedIn?: boolean;
  onSignIn?: () => void;
}): JSX.Element {
  if (signedIn) {
    return (
      <div
        role="status"
        data-testid="assistant-signed-in-again"
        className="mt-2 rounded-[10px] border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-2 text-[12.5px] text-primary-gray-light-700"
      >
        You&apos;re signed in again. Ask your question again to keep chatting.
      </div>
    );
  }
  return (
    <div
      role="alert"
      data-testid="assistant-sign-in-card"
      className="mt-2 rounded-[10px] border border-primary-warning-300 bg-primary-warning-50 px-3 py-[10px]"
    >
      <p className="text-[13px] font-semibold text-primary-gray-light-900">Your sign-in has expired</p>
      <p className="mt-1 text-[12.5px] text-primary-gray-light-600">
        Sign in again to keep chatting. This conversation is saved.
      </p>
      <button
        type="button"
        onClick={onSignIn}
        className="mt-[10px] rounded-lg border-none bg-sunrise px-[14px] py-2 text-[13px] font-semibold text-primary-gray-light-900 hover:bg-blossom"
      >
        Sign in
      </button>
    </div>
  );
}
